// src/admin/AdminRevenue.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";

const PRESETS = [
  { key: "7d", label: "Last 7d", days: 7 },
  { key: "30d", label: "Last 30d", days: 30 },
  { key: "90d", label: "Last 90d", days: 90 },
  { key: "ytd", label: "YTD", ytd: true },
  { key: "all", label: "All time", all: true },
];

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
function startOfYear() {
  const d = startOfToday();
  d.setMonth(0, 1);
  return d;
}
function addDays(base, days) {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}
function presetRange(key) {
  const t = startOfToday();
  if (key === "ytd") return { from: startOfYear(), to: addDays(t, 1) };
  if (key === "all") return { from: null, to: null };
  const def = PRESETS.find((p) => p.key === key) || PRESETS[1]; // default 30d
  return { from: addDays(t, -(def.days - 1)), to: addDays(t, 1) };
}

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function money(n) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}
function tsToDate(ts) {
  if (!ts) return null;
  return typeof ts?.toDate === "function" ? ts.toDate() : new Date(ts);
}

export default function AdminRevenue() {
  const [preset, setPreset] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]); // paid bookings

  const range = useMemo(() => presetRange(preset), [preset]);

  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      setErr("");
      try {
        const col = collection(db, "bookings");
        const hasWindow = !!range.from && !!range.to;

        // Preferred indexed query (status + paidAt >= from, orderBy paidAt)
        // If index missing, we catch and fall back to simple query.
        let snap;
        try {
          if (hasWindow) {
            const q1 = query(
              col,
              where("status", "==", "paid"),
              where("paidAt", ">=", range.from),
              orderBy("paidAt", "desc")
            );
            snap = await getDocs(q1);
          } else {
            const q1 = query(col, where("status", "==", "paid"), orderBy("paidAt", "desc"));
            snap = await getDocs(q1);
          }
        } catch (e) {
          // Fallback: only filter by status; filter dates client-side
          const q2 = query(col, where("status", "==", "paid"));
          snap = await getDocs(q2);
        }

        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const filtered = hasWindow
          ? raw.filter((b) => {
              const paid = tsToDate(b.paidAt) || tsToDate(b.createdAt);
              return paid && paid >= range.from && paid < range.to;
            })
          : raw;

        // Sort desc by paidAt (fallback createdAt)
        filtered.sort((a, b) => {
          const A = tsToDate(a.paidAt) || tsToDate(a.createdAt) || new Date(0);
          const B = tsToDate(b.paidAt) || tsToDate(b.createdAt) || new Date(0);
          return B - A;
        });

        if (!alive) return;
        setRows(filtered);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setErr("Couldn't load revenue. Try again.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, [db, range.from, range.to]);

  // Totals
  const totals = useMemo(() => {
    let gross = 0;       // realized gross: amountTotal - refunds
    let ourCut = 0;      // platformFee - platformFeeRefunded
    let hostEarn = 0;    // hostEarnings or (gross - ourCut)
    let count = 0;

    for (const b of rows) {
      const g = num(b.amountTotal);
      const r = num(b.amountRefunded); // optional
      const fee = num(b.platformFee);
      const feeR = num(b.platformFeeRefunded); // optional
      const host = num(b.hostEarnings);

      const realizedGross = Math.max(0, g - r);
      const realizedFee = Math.max(0, fee - feeR);
      const realizedHost = host || Math.max(0, realizedGross - realizedFee);

      gross += realizedGross;
      ourCut += realizedFee;
      hostEarn += realizedHost;
      count += 1;
    }
    const aov = count ? gross / count : 0;
    return { gross, ourCut, hostEarn, count, aov };
  }, [rows]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Revenue</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Gross, platform fees, and host payouts for paid bookings.
          </p>
        </div>

        {/* Preset range picker */}
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`rounded-full border px-3 py-1.5 text-sm ${
                preset === p.key
                  ? "border-black bg-black text-white"
                  : "border-neutral-300 hover:bg-neutral-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      {err && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi title="Gross" value={loading ? "—" : money(totals.gross)} />
        <Kpi title="Our cut" value={loading ? "—" : money(totals.ourCut)} />
        <Kpi title="Host earnings" value={loading ? "—" : money(totals.hostEarn)} />
        <Kpi title="Paid bookings" value={loading ? "—" : totals.count} />
        <Kpi title="AOV" value={loading ? "—" : money(totals.aov)} />
      </div>

      {/* Recent bookings table */}
      <section className="rounded-2xl border border-neutral-200 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Paid bookings{preset !== "all" ? ` (${rows.length})` : ""}</h2>
          <ExportCsv rows={rows} />
        </div>

        {loading ? (
          <div className="animate-pulse text-sm text-neutral-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-600">No paid bookings in this range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-neutral-500">
                <tr>
                  <Th>ID</Th>
                  <Th>Listing</Th>
                  <Th>Paid at</Th>
                  <Th>Gross</Th>
                  <Th>Fee (our cut)</Th>
                  <Th>Host</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 100).map((b) => {
                  const paidAt = tsToDate(b.paidAt) || tsToDate(b.createdAt);
                  const gross = Math.max(0, num(b.amountTotal) - num(b.amountRefunded));
                  const fee = Math.max(0, num(b.platformFee) - num(b.platformFeeRefunded));
                  const host = num(b.hostEarnings) || Math.max(0, gross - fee);
                  return (
                    <tr key={b.id} className="border-t">
                      <Td className="font-mono">{b.id}</Td>
                      <Td className="truncate">{b.listingId || "—"}</Td>
                      <Td>{paidAt ? paidAt.toLocaleString() : "—"}</Td>
                      <Td>{money(gross)}</Td>
                      <Td>{money(fee)}</Td>
                      <Td>{money(host)}</Td>
                      <Td>{b.status || "—"}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length > 100 && (
              <div className="mt-3 text-xs text-neutral-500">
                Showing first 100 rows. Use Export to download all in range.
              </div>
            )}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-neutral-200 p-6 text-sm text-neutral-600">
        <div className="font-semibold">Notes</div>
        <ul className="mt-2 list-disc pl-5">
          <li>We treat <span className="font-medium">Gross</span> as <em>amountTotal – amountRefunded</em>.</li>
          <li><span className="font-medium">Our cut</span> is <em>platformFee – platformFeeRefunded</em> (if present).</li>
          <li><span className="font-medium">Host earnings</span> is from <code>hostEarnings</code>, falling back to <em>Gross – Our cut</em>.</li>
          <li>For perfect accuracy, compute revenue server-side (Cloud Functions) or export to BigQuery and aggregate there.</li>
        </ul>
      </section>
    </div>
  );
}

function Kpi({ title, value }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold">{value ?? "—"}</div>
    </div>
  );
}
function Th({ children }) {
  return <th className="py-2 pr-4">{children}</th>;
}
function Td({ children, className }) {
  return <td className={`py-2 pr-4 ${className || ""}`}>{children}</td>;
}

function ExportCsv({ rows }) {
  function toCsv() {
    const header = [
      "id","listingId","status","paidAt","createdAt",
      "amountTotal","amountRefunded","platformFee","platformFeeRefunded","hostEarnings"
    ];
    const lines = [header.join(",")];
    for (const b of rows) {
      const paidAt = tsToDate(b.paidAt)?.toISOString() || "";
      const createdAt = tsToDate(b.createdAt)?.toISOString() || "";
      const cells = [
        b.id,
        b.listingId || "",
        b.status || "",
        paidAt,
        createdAt,
        num(b.amountTotal),
        num(b.amountRefunded),
        num(b.platformFee),
        num(b.platformFeeRefunded),
        num(b.hostEarnings),
      ];
      lines.push(cells.map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "revenue.csv";
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <button
      onClick={toCsv}
      className="rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
    >
      Export CSV
    </button>
  );
}
function csvEscape(x) {
  const s = String(x ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
